import React, { useState } from 'react';
import { Alert, Button, Form, Input, Tabs, message, Checkbox } from 'antd';
import { createStyles } from 'antd-style';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from './context/AuthContext';
import { api } from './utils/api';

const useStyles = createStyles(({ token }) => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'auto',
      backgroundImage:
        "url('https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/V-_oS6r-i7wAAAAAAAAAAAAAFl94AQBr')",
      backgroundSize: '100% 100%',
    },
    content: {
      flex: '1',
      padding: '32px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    form: {
      width: '328px',
      maxWidth: '75vw',
      padding: '32px',
      backgroundColor: 'white',
      borderRadius: token.borderRadius,
      boxShadow: token.boxShadow,
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px',
    },
    logo: {
      height: '44px',
      marginBottom: '16px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    subtitle: {
      color: token.colorTextSecondary,
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '24px',
    },
    footerBar: {
      textAlign: 'center',
      padding: '16px',
      color: token.colorTextSecondary,
    },
  };
});

const LoginMessage: React.FC<{
  content: string;
}> = ({ content }) => {
  return (
    <Alert
      style={{
        marginBottom: 24,
      }}
      message={content}
      type="error"
      showIcon
    />
  );
};

const Login: React.FC<{
  onLoginSuccess?: () => void;
}> = ({ onLoginSuccess }) => {
  const [loginStatus, setLoginStatus] = useState<string | undefined>(undefined);
  const [loginType, setLoginType] = useState<string>('account');
  const [loading, setLoading] = useState(false);
  const { styles } = useStyles();
  const { login } = useAuth();

  // 处理登录
  const handleLogin = async (values: any) => {
    setLoading(true);
    setLoginStatus(undefined);
    
    try {
      const data = await api.login({
        username: values.username,
        password: values.password,
      });
      
      if (data.status === 200 && data.data) {
        // 登录成功
        login(data.data.token, data.data.user);
        message.success('登录成功！');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        // 登录失败
        setLoginStatus('error');
        message.error(data.msg || '登录失败');
      }
    } catch (error) {
      console.error('登录请求失败:', error);
      setLoginStatus('error');
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理注册
  const handleRegister = async (values: any) => {
    setLoading(true);
    setLoginStatus(undefined);

    try {
      const data = await api.register({
        username: values.username,
        password: values.password,
        password2: values.password2,
      });
      
      if (data.status === 200 && data.data) {
        // 注册成功，自动登录
        login(data.data.token, data.data.user);
        message.success('注册成功！');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        // 注册失败
        setLoginStatus('error');
        message.error(data.msg || '注册失败');
      }
    } catch (error) {
      console.error('注册请求失败:', error);
      setLoginStatus('error');
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理忘记密码点击
  const handleForgotPassword = () => {
    alert('测试阶段未添加身份验证模块，请电话联系李医生18680495180验证并重置密码');
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.form}>
          <div className={styles.header}>
            <img src="./dakanglogo.png" alt="logo" className={styles.logo} />
            <div className={styles.title}>达康AI医疗辅助系统</div>
          </div>

          <Tabs
            activeKey={loginType}
            onChange={(activeKey) => {
              setLoginType(activeKey);
              setLoginStatus(undefined);
            }}
            centered
            items={[
              {
                key: 'account',
                label: '账户密码登录',
              },
              {
                key: 'register',
                label: '新用户注册',
              },
            ]}
          />

          {loginStatus === 'error' && loginType === 'account' && (
            <LoginMessage content="账户或密码错误" />
          )}

          {loginStatus === 'error' && loginType === 'register' && (
            <LoginMessage content="注册失败，请检查输入信息" />
          )}

          {loginType === 'account' && (
            <Form
              name="login"
              onFinish={handleLogin}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入手机号!' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="手机号" size="large" />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码！' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>

              <div className={styles.footer}>
                <a onClick={handleForgotPassword} style={{ cursor: 'pointer' }}>忘记密码</a>
              </div>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  登录
                </Button>
              </Form.Item>
            </Form>
          )}

          {loginType === 'register' && (
            <Form
              name="register"
              onFinish={handleRegister}
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入手机号!' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号！' }
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="手机号" size="large" />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码！' },
                  { min: 1, max: 16, message: '密码长度为1-16个字符!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="password2"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码！' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致！'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="确认密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[{ required: true, message: '请阅读并同意用户使用协议' }]}
                style={{ marginBottom: 0 }}
              >
                <Checkbox>
                  我已阅读并同意
                  <a href="/user-agreement" target="_blank" rel="noopener noreferrer">《用户使用协议》</a>
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="privacy"
                valuePropName="checked"
                rules={[{ required: true, message: '请阅读并同意平台隐私政策' }]}
                style={{ marginBottom: 0 }}
              >
                <Checkbox>
                  我已阅读并同意
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">《平台隐私政策》</a>
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  注册
                </Button>
              </Form.Item>
            </Form>
          )}
        </div>
      </div>
      <div className={styles.footerBar}>
        达康AI医疗辅助系统（试运行版） ©2025
      </div>
    </div>
  );
};

export default Login; 